import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { updateRentalPortalApplicant } from '../../../../../../api'
import { SERVER_NON_FIELD_ERROR_KEY } from '../../../../../../constants/validation'
import {
  useInlineNotifications,
  useNotification,
} from '../../../../../../hooks'
import {
  getIsMockApplicantId,
  patchMockApplicant,
  toApplicantUpdateData,
} from '../../../../../RentalPortalApplicant'
import { useApplicantProfileContext } from '../../../RentalPortalApplicantProfile.hooks'
import { APPLICANT_PROFILE_BLOCK_CONFLICT_NOTIFICATION_ID } from '../../RentalPortalApplicantProfileTabOverview.constants'
import {
  getApplicantBlockConflictNotification,
  getIsApplicantBlockConflictError,
} from '../../RentalPortalApplicantProfileTabOverview.utils'

export function useApplicantBlockSave({ onClose, onSuccess }) {
  const { t } = useTranslation()

  const { handleAddSuccessNotification } = useNotification()

  const { applicantId, handleEntityReload } = useApplicantProfileContext()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const submittedRef = useRef(null)

  function handleClickReload() {
    handleRemoveNotification(APPLICANT_PROFILE_BLOCK_CONFLICT_NOTIFICATION_ID)

    onClose()

    handleEntityReload()
  }

  function handleClickOverwrite() {
    handleRemoveNotification(APPLICANT_PROFILE_BLOCK_CONFLICT_NOTIFICATION_ID)

    handleSaveBlock(submittedRef.current)
  }

  async function patchApplicant(payload) {
    return getIsMockApplicantId(applicantId)
      ? patchMockApplicant(applicantId, payload)
      : updateRentalPortalApplicant({
          data: toApplicantUpdateData(payload),
          id: applicantId,
        })
  }

  async function handleSaveBlock({ formikHelpers, payload }) {
    submittedRef.current = { formikHelpers, payload }

    formikHelpers.setSubmitting(true)

    try {
      // TODO: send the applicant version once the detail serves one, so an overwrite after a 409 is versioned
      await patchApplicant(payload)
    } catch (error) {
      formikHelpers.setSubmitting(false)

      if (getIsApplicantBlockConflictError(error)) {
        handleAddNotification(
          getApplicantBlockConflictNotification({
            t,
            onClickOverwrite: handleClickOverwrite,
            onClickReload: handleClickReload,
          })
        )

        return
      }

      formikHelpers.setErrors({
        [SERVER_NON_FIELD_ERROR_KEY]: t(
          'RentalPortal.ApplicantProfileBlockSaveErrorKey'
        ),
      })

      return
    }

    await handleEntityReload().catch(console.error)

    handleAddSuccessNotification('Notification.CommonTitleSavedKey')

    formikHelpers.setSubmitting(false)

    onSuccess()
  }

  return {
    handleAddNotification,
    handleRemoveNotification,
    handleSaveBlock,
    notifications,
  }
}
